import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowUpRight, Check, X } from "lucide-react";
import { api, apiErrorCode } from "@/lib/api";
import { useAuth, type CurrentUser } from "@/lib/auth";

const schema = z
  .object({
    password: z
      .string()
      .min(8, "Mínimo 8 caracteres")
      .regex(/[a-zA-Z]/, "Debe incluir al menos una letra")
      .regex(/[0-9]/, "Debe incluir al menos un número"),
    confirm: z.string(),
  })
  .refine((v) => v.password === v.confirm, { path: ["confirm"], message: "No coincide" });

type Form = z.infer<typeof schema>;

const RULES = [
  { key: "length", label: "Al menos 8 caracteres", test: (v: string) => v.length >= 8 },
  { key: "letter", label: "Incluye una letra", test: (v: string) => /[a-zA-Z]/.test(v) },
  { key: "digit", label: "Incluye un número", test: (v: string) => /[0-9]/.test(v) },
];

export default function SetPasswordPage() {
  const { token = "" } = useParams();
  const nav = useNavigate();
  const setSession = useAuth((s) => s.setSession);
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<Form>({
    resolver: zodResolver(schema),
    mode: "onChange",
  });

  const password = watch("password") ?? "";
  const confirm = watch("confirm") ?? "";
  const confirmMatches = confirm.length > 0 && confirm === password;

  async function onSubmit(values: Form) {
    setServerError(null);
    try {
      const { data } = await api.post<{ access_token: string; user: CurrentUser }>(
        `/auth/set-password/${token}`,
        { password: values.password }
      );
      setSession(data.access_token, data.user);
      nav("/overview", { replace: true });
    } catch (err) {
      const code = apiErrorCode(err);
      setServerError(
        code === "INVALID_OR_EXPIRED_TOKEN"
          ? "El enlace ya no es válido. Pide una nueva invitación."
          : "No se pudo activar la cuenta"
      );
    }
  }

  return (
    <div className="min-h-[100dvh] bg-white flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-sm animate-fade-up">
        <div className="mb-14">
          <img
            src="https://raw.githubusercontent.com/Jefferspri/ponos-host-images/main/logo.png"
            alt="NiuFit Partners"
            className="h-8 w-auto"
            style={{ filter: "invert(1) brightness(0.05)" }}
          />
        </div>

        <div className="mb-10">
          <h2 className="font-display text-4xl font-medium tracking-tight leading-none">
            Activa tu<br />cuenta.
          </h2>
          <p className="text-sm text-muted-foreground mt-4">
            Elige una contraseña para acceder al panel.
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div className="space-y-1.5">
            <label className="block text-[11px] uppercase tracking-[0.15em] text-muted-foreground font-medium">
              Contraseña
            </label>
            <input
              type="password"
              autoComplete="new-password"
              placeholder="Elige una contraseña"
              className="w-full bg-transparent border-b border-border pb-2.5 pt-2 text-[15px] outline-none focus:border-ink transition-colors placeholder:text-muted-foreground/50"
              {...register("password")}
            />
          </div>

          {/* Checklist de requisitos */}
          <ul className="space-y-1.5 -mt-2">
            {RULES.map((r) => {
              const ok = r.test(password);
              return (
                <li key={r.key} className="flex items-center gap-2 text-xs">
                  <span
                    className={[
                      "inline-flex h-4 w-4 items-center justify-center rounded-full border transition-colors",
                      ok
                        ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                        : password.length === 0
                        ? "border-neutral-200 text-neutral-300"
                        : "border-neutral-200 text-neutral-400",
                    ].join(" ")}
                  >
                    {ok ? <Check className="h-2.5 w-2.5" strokeWidth={3} /> : <span className="h-1 w-1 rounded-full bg-current" />}
                  </span>
                  <span
                    className={[
                      "transition-colors",
                      ok ? "text-emerald-700" : "text-muted-foreground",
                    ].join(" ")}
                  >
                    {r.label}
                  </span>
                </li>
              );
            })}
          </ul>

          <div className="space-y-1.5">
            <label className="block text-[11px] uppercase tracking-[0.15em] text-muted-foreground font-medium">
              Confirmar
            </label>
            <input
              type="password"
              autoComplete="new-password"
              placeholder="Repite la contraseña"
              className="w-full bg-transparent border-b border-border pb-2.5 pt-2 text-[15px] outline-none focus:border-ink transition-colors placeholder:text-muted-foreground/50"
              {...register("confirm")}
            />
            {confirm.length > 0 && (
              <div
                className={[
                  "flex items-center gap-1.5 text-xs pt-1 transition-colors",
                  confirmMatches ? "text-emerald-700" : "text-destructive",
                ].join(" ")}
              >
                {confirmMatches ? (
                  <>
                    <Check className="h-3 w-3" strokeWidth={2.5} />
                    Las contraseñas coinciden
                  </>
                ) : (
                  <>
                    <X className="h-3 w-3" strokeWidth={2.5} />
                    No coincide con la contraseña
                  </>
                )}
              </div>
            )}
            {errors.confirm && confirm.length === 0 && (
              <p className="text-xs text-destructive pt-1">{errors.confirm.message}</p>
            )}
          </div>

          {serverError && (
            <div className="rounded border border-destructive/25 bg-destructive/[0.03] px-3 py-2 text-sm text-destructive">
              {serverError}
            </div>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            className="group relative w-full h-14 rounded-md bg-ink text-white font-semibold overflow-hidden transition-all duration-300 shadow-[0_1px_2px_rgba(0,0,0,0.08)] hover:shadow-[0_16px_32px_-12px_rgba(0,0,0,0.35),_0_0_0_1px_rgba(193,199,206,0.35)] active:translate-y-[1px] disabled:opacity-80 disabled:cursor-wait"
          >
            <span
              aria-hidden
              className="pointer-events-none absolute inset-y-0 -left-1/3 w-1/3 -skew-x-12 bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 transition-all duration-700 group-hover:opacity-100 group-hover:translate-x-[380%]"
            />
            <span className="relative flex items-center justify-between px-6">
              <span className="text-[15px] tracking-tight inline-flex items-baseline gap-0.5">
                {isSubmitting ? "Activando" : "Activar cuenta"}
                {isSubmitting && (
                  <span aria-hidden className="inline-flex gap-0.5 ml-0.5">
                    <span className="h-1 w-1 rounded-full bg-silver-hi/70 animate-pulse-dot" style={{ animationDelay: "0ms" }} />
                    <span className="h-1 w-1 rounded-full bg-silver-hi/70 animate-pulse-dot" style={{ animationDelay: "200ms" }} />
                    <span className="h-1 w-1 rounded-full bg-silver-hi/70 animate-pulse-dot" style={{ animationDelay: "400ms" }} />
                  </span>
                )}
              </span>
              <ArrowUpRight
                className={[
                  "h-4 w-4 text-silver-hi transition-all duration-300",
                  isSubmitting ? "opacity-40" : "group-hover:translate-x-1 group-hover:-translate-y-1",
                ].join(" ")}
                strokeWidth={2.25}
              />
            </span>
            {isSubmitting && (
              <span
                aria-hidden
                className="pointer-events-none absolute bottom-0 left-0 right-0 h-[2px] overflow-hidden"
              >
                <span className="absolute inset-y-0 w-1/4 bg-gradient-to-r from-transparent via-silver-hi to-transparent animate-slide-progress" />
              </span>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}
